<template>
	<theme-provider :theme="theme">
		<div v-if="isLoading" />
		<div v-if="!isLoading">
			<div v-if="$route.meta.layout === 'page'">
				<app-header
					:menuItems="mainMenu.items" :logoUrl="theme.logoUrl" v-if="layoutMode === 'standalone'" />
				<div class="app">
					<router-view />
				</div>
			</div>
			<div class="app" v-if="$route.meta.layout === 'plain'">
				<router-view />
			</div>
			<app-footer :imprintLink="footerLinks.imprintLink" :privacyLink="footerLinks.privacyLink"
				v-if="layoutMode === 'standalone'" />
		</div>
	</theme-provider>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { ThemeProvider } from 'vue-styled-components';
import AppHeader from '@/components/global/header.vue';
import AppFooter from '@/components/global/footer.vue';

export default {
	name: 'App',

	components: {
		AppHeader,
		ThemeProvider,
		AppFooter,
	},

	computed: {
		...mapGetters({
			mainMenu: 'general/getMainMenu',
			footerLinks: 'general/getFooterLinks',
			theme: 'general/getTheme',
		}),
		layoutMode() {
			const params = new URLSearchParams(window.location.search);
			const layoutMode = params.get('layoutMode');

			if (layoutMode === 'widget') {
				return layoutMode;
			}

			// fallback to standalone
			return 'standalone';
		},
	},

	data() {
		return {
			isLoading: true,
		};
	},

	async mounted() {
		this.isLoading = true;
		if (this.layoutMode === 'standalone') {
			await this.fetchMainMenu();
		}
		this.isLoading = false;

		this.processPendingCheckout();
	},

	methods: {
		...mapActions('general', {
			fetchMainMenu: 'fetchMainMenu',
		}),

		/**
		 * When the page is opened with ?openCart=true (redirected from an
		 * iframe), eagerly load Snipcart, add the pending product stored
		 * in localStorage, and open the cart for checkout.
		 *
		 * This works around a PayPal Express Checkout limitation that
		 * blocks checkout flows inside iframes.
		 */
		async processPendingCheckout() {
			const params = new URLSearchParams(window.location.search);
			if (params.get('openCart') !== 'true') return;

			const raw = localStorage.getItem('pendingSnipcartCheckout');
			if (!raw) return;

			try {
				const productDefinition = JSON.parse(raw);
				localStorage.removeItem('pendingSnipcartCheckout');

				// Clean the openCart param from the URL so a page refresh
				// doesn't re-trigger the checkout flow.
				params.delete('openCart');
				const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
				window.history.replaceState({}, '', cleanUrl);

				// Force-load Snipcart immediately (instead of on-user-interaction)
				if (typeof window.LoadSnipcart === 'function') {
					window.LoadSnipcart();
				}

				// Wait for the Snipcart SDK to be fully ready
				await this.waitForSnipcart();

				// Add the item and let Snipcart open the cart overlay
				await Snipcart.api.cart.items.add(productDefinition);
			} catch (error) {
				console.error('Failed to process pending checkout:', error);
			}
		},

		/**
		 * Returns a promise that resolves once the Snipcart SDK is ready.
		 */
		waitForSnipcart() {
			return new Promise((resolve) => {
				const check = () => {
					if (typeof Snipcart !== 'undefined' && Snipcart.api) {
						resolve();
					} else {
						setTimeout(check, 100);
					}
				};
				check();
			});
		},
	},
};
</script>
